import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import App from "./App.tsx";
import "./index.css";
import { startClickTracking } from "./lib/startClickTracking";

startClickTracking();

// GTM kill-switch sur les routes /admin/* (même après navigation SPA depuis la homepage)
(function installAdminGtmGuard() {
  const isAdminPath = () => {
    const p = window.location.pathname || "";
    return p === "/admin" || p.startsWith("/admin/") || p === "/admin-login";
  };

  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  const originalPush = w.dataLayer.push.bind(w.dataLayer);

  // Intercepte tous les push : bloque si on est sur une route admin
  w.dataLayer.push = function (...args: any[]) {
    if (isAdminPath()) return 0;
    return originalPush(...args);
  };

  // Notifie GTM lors des changements de route SPA pour qu'il purge ses tags si besoin
  const notifyRouteChange = () => {
    if (isAdminPath()) {
      // Désactive également gtag si présent
      if (typeof w.gtag === "function") {
        w.gtag = function () { /* noop on admin */ };
      }
    }
  };

  const origPush = history.pushState;
  const origReplace = history.replaceState;
  history.pushState = function (...a: any[]) {
    const r = origPush.apply(this, a as any);
    notifyRouteChange();
    return r;
  };
  history.replaceState = function (...a: any[]) {
    const r = origReplace.apply(this, a as any);
    notifyRouteChange();
    return r;
  };
  window.addEventListener("popstate", notifyRouteChange);
})();

// GTM: track all phone call clicks globally
document.addEventListener("click", (e) => {
  const target = e.target as HTMLElement;
  const telLink = target.closest?.("a[href^='tel:']") as HTMLAnchorElement | null;
  if (telLink) {
    (window as any).dataLayer?.push({
      event: "phone_call_click",
      phone_number: telLink.href.replace("tel:", ""),
      click_location: telLink.closest("header") ? "header" :
                      telLink.closest("footer") ? "footer" : "page",
    });
    return;
  }

  // Track mailto clicks
  const mailLink = target.closest?.("a[href^='mailto:']") as HTMLAnchorElement | null;
  if (mailLink) {
    (window as any).dataLayer?.push({
      event: "email_click",
      email_address: mailLink.href.replace("mailto:", ""),
      click_location: mailLink.closest("header") ? "header" :
                      mailLink.closest("footer") ? "footer" : "page",
    });
    return;
  }

  // Track CTA devis clicks (links/buttons pointing to /devis)
  const devisLink = target.closest?.("a[href='/devis'], a[href^='/devis?']") as HTMLAnchorElement | null;
  if (devisLink) {
    (window as any).dataLayer?.push({
      event: "cta_devis_click",
      click_text: devisLink.textContent?.trim() || "",
      click_location: devisLink.closest("header") ? "header" :
                      devisLink.closest("footer") ? "footer" :
                      devisLink.closest("section") ? "section" : "page",
    });
  }
});

// GTM: scroll depth tracking (25%, 50%, 75%, 100%)
const scrollThresholds = [25, 50, 75, 100];
const firedThresholds = new Set<number>();

window.addEventListener("scroll", () => {
  const scrollTop = window.scrollY;
  const docHeight = document.documentElement.scrollHeight - window.innerHeight;
  if (docHeight <= 0) return;
  const percent = Math.round((scrollTop / docHeight) * 100);

  for (const threshold of scrollThresholds) {
    if (percent >= threshold && !firedThresholds.has(threshold)) {
      firedThresholds.add(threshold);
      (window as any).dataLayer?.push({
        event: "scroll_depth",
        scroll_percentage: threshold,
        page_path: window.location.pathname,
      });
    }
  }
}, { passive: true });
createRoot(document.getElementById("root")!).render(
  <HelmetProvider>
    <App />
  </HelmetProvider>
);
